import omit from 'lodash/omit';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import React from 'react';
import MissingOrInvalidFieldsDetails from './MissingOrInvalidFieldsDetails';
import NotEnoughInformation from './NotEnoughInformation';
import { SCREENING_METHOD_STATUS } from './ProviderScreening/constants';
import { SCREENING_ERROR_REGEX } from './constants';
import RecommendedScreeningResult from './RecommendedScreeningResult';
import CanceledDetail from './CanceledDetail';
import messages from './messages';

// Maps the raw API status enum to the Fortress display status string.
// Mirrors FORTRESS_RESULT_STATUSES in the lambdas so the frontend can
// derive fortressStatus from status when the stored field is absent.
const STATUS_TO_FORTRESS_STATUS = {
  APPROVED: 'Accept',
  APPROVED_WITH_CONDITIONS: 'Accept with Conditions',
  DECLINED: 'Fail',
  REFER: 'Refer',
  PENDING: 'Pending',
  CANCELED: 'Canceled',
  CONDITIONAL: 'Refer',
  ERROR: 'Error',
};

function resolveDetailFortressStatus(resultDetail) {
  if (resultDetail?.fortressStatus) return resultDetail.fortressStatus;
  if (resultDetail?.status)
    return (
      STATUS_TO_FORTRESS_STATUS[resultDetail.status] ?? resultDetail.status
    );
  return null;
}

export function mapFortressStatusToBadgeVariant(fortressStatus) {
  const fortressStatusKey = fortressStatus?.toUpperCase?.().replace(/ /g, '_');
  if (!fortressStatusKey) {
    return null;
  }

  return SCREENING_METHOD_STATUS[fortressStatusKey] ?? null;
}

export function getScreeningMethodStatus({
  fortressStatus,
  missingOrInvalidFields = [],
  isResident,
}) {
  if (!isResident && missingOrInvalidFields.length > 0) {
    return SCREENING_METHOD_STATUS.NOT_READY;
  }

  return (
    mapFortressStatusToBadgeVariant(fortressStatus) ??
    SCREENING_METHOD_STATUS.NOT_STARTED
  );
}

export function getScreeningMethodDetails({
  fortressStatus,
  recommendations,
  providerName,
  intl,
  applicantValidationErrors,
  applicationStatus,
  isResident,
}) {
  if (
    !isResident &&
    applicantValidationErrors?.missingOrInvalidFields.length > 0
  )
    return (
      <MissingOrInvalidFieldsDetails
        intl={intl}
        fields={applicantValidationErrors.missingOrInvalidFields}
        applicantNames={
          applicantValidationErrors.applicantsWithMissingOrInvalidFields
        }
      />
    );

  if (fortressStatus === 'Canceled') {
    return (
      <CanceledDetail intl={intl} isDenied={applicationStatus === 'Denied'} />
    );
  }

  if (fortressStatus === 'Error') {
    return null;
  }

  // Sadly have to do this provider specific case here
  if (fortressStatus === 'Refer' && providerName === 'TrueWork') {
    return (
      <NotEnoughInformation
        intl={intl}
        message={recommendations.applicationRecommendation}
      />
    );
  }

  if (
    recommendations?.detailRecommendation ||
    recommendations?.applicationRecommendation
  ) {
    return (
      <RecommendedScreeningResult
        intl={intl}
        detailRecommendation={recommendations.detailRecommendation}
        applicationRecommendation={recommendations.applicationRecommendation}
        adverseFactors={recommendations.adverseFactors}
      />
    );
  }

  return null;
}

export function mapScreeningMethod({
  name,
  fortressStatus,
  providerName,
  recommendations,
  applicantValidationErrors,
  intl,
  applicationStatus,
  isResident,
  tooltipText,
  phasedCriminal,
  phasedCriminalDetails,
}) {
  return {
    name,
    ...(tooltipText ? { tooltipText } : {}),
    ...(phasedCriminal ? { phasedCriminal: true } : {}),
    status: getScreeningMethodStatus({
      fortressStatus,
      isResident,
      missingOrInvalidFields:
        applicantValidationErrors?.missingOrInvalidFields ?? [],
    }),
    // After an agent decline the criminal tile explains why it never ran.
    details:
      phasedCriminalDetails ??
      getScreeningMethodDetails({
        fortressStatus,
        recommendations,
        applicantValidationErrors,
        providerName,
        intl,
        applicationStatus,
        isResident,
      }),
  };
}

/** The tooltip for a criminal method that a phased screening has not returned yet. */
function phasedCriminalTooltip({ intl, isCriminalAwaiting }) {
  if (isCriminalAwaiting)
    return intl.formatMessage(messages.criminalReportAwaiting);
  return undefined;
}

// TransUnion holds a screening at phase 1 when it withholds the criminal
// report, even if the profile flag is off, so a phase on a TransUnion profile
// also marks the profile as phased.
export function isPhasedTransUnionProfile(
  providerPropertyProfile,
  screeningPhase,
) {
  // Only TransUnion screens in phases. The flag marks a phased profile, and a
  // phase marks a hold that TransUnion started while the flag was off.
  if (providerPropertyProfile?.provider?.name !== 'TransUnion') return false;
  return (
    providerPropertyProfile.isPhasedScreening === true || screeningPhase != null
  );
}

export function mapProviderPropertyProfileToScreeningProvider({
  providerPropertyProfile,
  screeningStatus,
  applicantValidationErrors,
  intl,
  applicationStatus,
  isResident,
  screeningPhase,
}) {
  const providerName = providerPropertyProfile.provider?.name;
  const applicationRecommendation =
    providerPropertyProfile.request?.result?.applicationRecommendation;
  const isRequetNotStarted =
    providerPropertyProfile.request?.status === 'NOT_STARTED';
  // Only treat as an error when there is an actual result — a request with
  // status ERROR but no result means the provider was never able to run
  // (e.g. a previous provider validation error halted the flow). In that
  // case we fall through to NOT_STARTED rendering, matching the production
  // behaviour where the provider simply wasn't reached.
  const isRequestError =
    providerPropertyProfile.request?.status === 'ERROR' &&
    providerPropertyProfile.request?.result != null;
  const isRequestInProgress =
    providerPropertyProfile.request?.status === 'IN_PROGRESS';

  const methods = (providerPropertyProfile.screeningMethods ?? []).map(
    ({ method }) => {
      const providerPropertyProfileResponse =
        providerPropertyProfile.request?.result;
      const resultDetail = providerPropertyProfileResponse?.details?.find(
        (d) => d.screeningMethod.id === method.id,
      );
      const isTransUnionCredit =
        providerName === 'TransUnion' && method.method === 'Credit';
      // A phased TransUnion screening has no criminal detail until phase 2
      // completes. The tile reads Not Started through the hold and after a
      // decline, Pending while phase 2 is in flight, and never inherits the
      // phase 1 result status.
      const requestStatus = providerPropertyProfile.request?.status;
      const isPhasedCriminalMissing =
        providerName === 'TransUnion' &&
        method.method === 'Criminal' &&
        !resultDetail &&
        isPhasedTransUnionProfile(providerPropertyProfile, screeningPhase);
      const isCriminalAwaiting =
        isPhasedCriminalMissing &&
        screeningPhase === 2 &&
        ['PENDING', 'IN_PROGRESS'].includes(requestStatus);
      const isCriminalNotStarted =
        isPhasedCriminalMissing && !isCriminalAwaiting;
      // The phase 1 recommendation belongs to credit and eviction, so a phased
      // criminal tile without a detail keeps only the missing-fields, canceled,
      // and error details.
      const recommendations = isPhasedCriminalMissing
        ? { adverseFactors: [] }
        : {
            applicationRecommendation,
            detailRecommendation: resultDetail?.detailRecommendation,
            adverseFactors: isTransUnionCredit
              ? providerPropertyProfileResponse?.adverseFactors ?? []
              : [],
          };
      // The marker hides the "automatically stopped" explanation, so it stays
      // off when the screening stopped before TransUnion ran or TransUnion
      // declined at phase 1.
      const requestResultStatus =
        providerPropertyProfile.request?.result?.status;
      const isPhasedCriminalTile =
        isPhasedCriminalMissing &&
        screeningPhase != null &&
        requestResultStatus !== 'DECLINED';

      let fortressStatus;
      if (screeningStatus === 'CANCELED' && isRequetNotStarted) {
        fortressStatus = 'Canceled';
      } else if (isRequestError) {
        fortressStatus = 'Error';
      } else if (isCriminalAwaiting) {
        fortressStatus = 'Pending';
      } else if (isCriminalNotStarted) {
        fortressStatus = null;
      } else if (resultDetail) {
        fortressStatus = resolveDetailFortressStatus(resultDetail);
      } else {
        fortressStatus =
          resolveDetailFortressStatus(providerPropertyProfileResponse) ??
          (isRequestInProgress ? 'Pending' : null);
      }

      return mapScreeningMethod({
        name: method.method,
        fortressStatus,
        providerName,
        applicantValidationErrors,
        recommendations,
        intl,
        applicationStatus,
        isResident,
        tooltipText: phasedCriminalTooltip({ intl, isCriminalAwaiting }),
        phasedCriminal: isPhasedCriminalTile,
        phasedCriminalDetails:
          isPhasedCriminalTile && requestStatus === 'DECLINED'
            ? intl.formatMessage(messages.criminalNotStartedAfterDecline)
            : undefined,
      });
    },
  );

  return {
    name: providerName,
    methods: sortBy(methods, 'name'),
    order: providerPropertyProfile.order,
  };
}

/**
 * Splits a phased provider into a methods box and a criminal box.
 * The criminal box carries the phasedCriminal marker.
 * A provider that is not phased, or has no Criminal method, stays whole.
 */
export function splitPhasedProvider(provider, isPhasedScreening) {
  const whole = [{ ...provider, key: String(provider.order) }];
  if (!isPhasedScreening) return whole;
  const criminalMethods = provider.methods.filter((m) => m.name === 'Criminal');
  if (criminalMethods.length === 0) return whole;
  const otherMethods = provider.methods.filter((m) => m.name !== 'Criminal');
  const criminalBox = {
    ...provider,
    key: `${provider.order}-criminal`,
    methods: criminalMethods,
    phasedCriminal: true,
  };
  if (otherMethods.length === 0) return [criminalBox];
  return [
    { ...provider, key: String(provider.order), methods: otherMethods },
    criminalBox,
  ];
}

export function getProviderProfilesFromHouseholdScreening({
  householdScreening,
}) {
  const providerProfiles = householdScreening?.requests?.map((request) => ({
    ...request.providerPropertyProfile,
    request: omit(request, ['providerPropertyProfile']),
  }));
  return providerProfiles?.length > 0 ? uniqBy(providerProfiles, 'id') : null;
}

export function getScreeningApplicationErrors(intl, request, applicants = []) {
  const errors = [];
  const requestErrorMessagess = request?.result?.notes ?? '';

  const applicantKeys = [
    ...requestErrorMessagess.matchAll(SCREENING_ERROR_REGEX.APPLICANTS),
  ].map((match) => match[1]);
  const applicantsNeedingReview = uniq(
    applicantKeys
      .map((key) => {
        const applicant = applicants[key - 1] ?? applicants[0];
        if (applicant) {
          return `${applicant.firstName} ${applicant.lastName}`;
        }
        return null;
      })
      .filter((name) => name !== null),
  );

  // Check for rent amount errors
  if (requestErrorMessagess.match(SCREENING_ERROR_REGEX.RENT_AMOUNT)) {
    errors.push(intl.formatMessage(messages.rentAmount));
  }

  // Check for employment income errors
  if (requestErrorMessagess.match(SCREENING_ERROR_REGEX.EMPLOYMENT_INCOME)) {
    errors.push(intl.formatMessage(messages.employmentIncome));
  }

  // Address errors
  if (requestErrorMessagess.match(SCREENING_ERROR_REGEX.ADDRESS)) {
    errors.push(intl.formatMessage(messages.invalidAddress));
  }
  if (requestErrorMessagess.match(SCREENING_ERROR_REGEX.CITY)) {
    errors.push(intl.formatMessage(messages.invalidCity));
  }
  if (requestErrorMessagess.match(SCREENING_ERROR_REGEX.STATE)) {
    errors.push(intl.formatMessage(messages.invalidState));
  }
  if (requestErrorMessagess.match(SCREENING_ERROR_REGEX.ZIP_CODE)) {
    errors.push(intl.formatMessage(messages.invalidPostalCode));
  }

  // If no specific errors found, add general error
  if (errors.length === 0) {
    errors.push(intl.formatMessage(messages.generalError));
  }

  const generalReviewMessage = intl.formatMessage(messages.reviewAndUpdate);

  return {
    errors,
    applicants: applicantsNeedingReview,
    message: generalReviewMessage,
  };
}
